import { Alert, Button, Card, Descriptions, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Recipient = {
  id: string;
  student_id?: string | null;
  email?: string | null;
  rendered_title: string;
  status: string;
  error_message?: string | null;
};

type Send = {
  id: string;
  send_no: string;
  status: string;
  template_code?: string;
  template_name?: string;
  channel?: string;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  summary?: string | null;
  body_override?: string | null;
  students: Array<{ student_id: string; admission_no: string; first_name: string; last_name?: string }>;
  recipients: Recipient[];
};

export default function CommsSendDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.comms.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-comm-send', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Send>(`/education/comms/sends/${id}`)).data,
  });
  const row = query.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-comm-send', id] });
    queryClient.invalidateQueries({ queryKey: ['education-comm-sends'] });
  };

  const dispatch = useMutation({
    mutationFn: () => api.post(`/education/comms/sends/${id}/dispatch`),
    onSuccess: () => {
      message.success(t('commSendDispatched'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const cancel = useMutation({
    mutationFn: () => api.post(`/education/comms/sends/${id}/cancel`),
    onSuccess: () => {
      message.success(t('commSendCancelled'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={row?.send_no || t('commSends')}
      description={t('commSendsIntro')}
      tags={row ? <Tag>{row.status}</Tag> : null}
      extra={<Button onClick={() => navigate('/education/comms/sends')}>{t('commSends')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('commsOutboundHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('template')}>
                {row.template_code || row.template_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('channel')}>{row.channel || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('sent')}>{row.sent_count}</Descriptions.Item>
              <Descriptions.Item label={t('failed')}>
                {row.failed_count} / {t('skipped')}: {row.skipped_count}
              </Descriptions.Item>
              <Descriptions.Item label={t('summary')} span={2}>
                {row.summary || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('messageOverride')} span={2}>
                {row.body_override || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
          {canManage && row?.status === 'draft' ? (
            <Space style={{ marginTop: 12 }}>
              <Button type="primary" loading={dispatch.isPending} onClick={() => dispatch.mutate()}>
                {t('dispatchCommSend')}
              </Button>
              <Button danger loading={cancel.isPending} onClick={() => cancel.mutate()}>
                {t('cancel')}
              </Button>
            </Space>
          ) : null}
        </Card>
        <Card size="small" title={t('students')}>
          <Table
            rowKey="student_id"
            size="small"
            pagination={false}
            dataSource={row?.students ?? []}
            columns={[
              { title: t('admissionNo'), dataIndex: 'admission_no', width: 120 },
              {
                title: t('student'),
                render: (_: unknown, s: { first_name: string; last_name?: string }) =>
                  [s.first_name, s.last_name].filter(Boolean).join(' '),
              },
            ]}
          />
        </Card>
        <Card size="small" title={t('recipients')}>
          <Table
            rowKey="id"
            size="small"
            pagination={{ pageSize: 50 }}
            dataSource={row?.recipients ?? []}
            columns={[
              { title: t('title'), dataIndex: 'rendered_title' },
              { title: t('email'), dataIndex: 'email', render: (v: string) => v || '—' },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('error'),
                dataIndex: 'error_message',
                render: (v: string) => v || '—',
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
